import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { createEditor, Transforms, Editor } from 'slate';
import { Slate, Editable, withReact, ReactEditor } from 'slate-react';
import clsx from 'clsx';
import { withHistory } from 'slate-history';
import { PlusIcon } from '@heroicons/react/24/outline';

import Button from '@dashboard/components/Button';
import Dialog from '@dashboard/components/Dialog';

import { loadSlateContent } from '@dashboard/lib/slate/load-slate-content';

import VariableDialog from './components/VariableDialog';
import PipingTag from './components/PipingTag';
import { withVariables } from './plugins/withVariables';

const VARIABLE_NODE_TYPE = 'variable';
const CONDITION_NODE_TYPE = 'condition';

// Custom element renderer with dialog position context
const ElementWithContext = props => (
  <Element {...props} editor={props.editor} variables={props.variables} />
);

// Custom element renderer
const Element = ({ attributes, children, element, editor, variables }) => {
  const [showEditDialog, setShowEditDialog] = useState(false);
  const conditionRef = useRef(null);

  switch (element.type) {
    case VARIABLE_NODE_TYPE:
      return (
        <span {...attributes} contentEditable={false}>
          <PipingTag>{element.data.name}</PipingTag>
          {children}
        </span>
      );
    case CONDITION_NODE_TYPE:
      return (
        <div {...attributes} className="my-2 pl-4 border-l-2 border-blue-300">
          <div
            ref={conditionRef}
            contentEditable={false}
            className="bg-blue-50 text-blue-700 rounded-md text-xs px-2 py-1 cursor-pointer hover:bg-blue-100"
            onClick={e => {
              e.preventDefault();
              setShowEditDialog(true);
            }}
          >
            if {element.data.variable.name} {element.data.operator} {element.data.value}
          </div>
          <div>{children}</div>

          {variables && (
            <EditConditionDialog
              isOpen={showEditDialog}
              onClose={() => setShowEditDialog(false)}
              initialVariable={element.data.variable}
              initialOperator={element.data.operator}
              initialValue={element.data.value}
              variables={variables}
              onSave={(variable, operator, value) => {
                const path = ReactEditor.findPath(editor, element);
                Transforms.setNodes(
                  editor,
                  {
                    data: { variable, operator, value },
                  },
                  { at: path },
                );
                setShowEditDialog(false);
                ReactEditor.focus(editor);
              }}
            />
          )}
        </div>
      );
    default:
      return <p {...attributes}>{children}</p>;
  }
};

// Custom leaf renderer
const Leaf = ({ attributes, children }) => {
  return <span {...attributes}>{children}</span>;
};

function getSlateValue(value) {
  try {
    const parsedValue = typeof value === 'string' ? JSON.parse(value) : value;
    return loadSlateContent(parsedValue);
  } catch (e) {
    return [{ type: 'paragraph', children: [{ text: '' }] }];
  }
}

const RichTextarea = ({
  values,
  name,
  id,
  setFieldValue,
  variables = [],
  appearance = 'default',
}) => {
  const editor = useMemo(() => withVariables(withHistory(withReact(createEditor()))), []);
  const [value, setValue] = useState(getSlateValue(values[name]));

  const editorRef = useRef(null);
  const dialogRef = useRef(null);
  const addVariableButtonRef = useRef(null);
  const firstVariableItemRef = useRef(null);
  const [showVariableDialog, setShowVariableDialog] = useState(false);
  const [dialogPosition, setDialogPosition] = useState({ top: 0, left: 0 });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [justInserted, setJustInserted] = useState(false);
  const [showConditionDialog, setShowConditionDialog] = useState(false);

  const DIALOG_WIDTH = 200;

  const onKeyDown = useCallback(
    event => {
      if (event.key === '{') {
        event.preventDefault();
        const domSelection = window.getSelection();
        const range = domSelection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        const editorRect = editorRef.current?.getBoundingClientRect();

        if (editorRect) {
          let top = rect.bottom - editorRect.top;
          let left = rect.left - editorRect.left;

          if (left + DIALOG_WIDTH > editorRect.width) {
            left = editorRect.width - DIALOG_WIDTH;
          }

          setDialogPosition({ top, left });
          setShowVariableDialog(true);
          setSelectedIndex(0);
          setTimeout(() => firstVariableItemRef.current?.focus(), 0);
        }
        return;
      }

      if (showVariableDialog) {
        switch (event.key) {
          case '}':
          case 'Escape':
            event.preventDefault();
            setShowVariableDialog(false);
            return;
          case 'ArrowDown':
            event.preventDefault();
            setSelectedIndex(prevIndex =>
              prevIndex < variables.length - 1 ? prevIndex + 1 : prevIndex,
            );
            return;
          case 'ArrowUp':
            event.preventDefault();
            setSelectedIndex(prevIndex => (prevIndex > 0 ? prevIndex - 1 : prevIndex));
            return;
          case 'Enter':
            event.preventDefault();
            onClickBlock(variables[selectedIndex]);
            return;
          default:
            return;
        }
      }
    },
    [showVariableDialog, variables, selectedIndex],
  );

  const insertVariable = useCallback(
    variable => {
      const variableNode = {
        type: VARIABLE_NODE_TYPE,
        data: { name: variable.name, id: variable.id },
        children: [{ text: '' }], // must have a text child
      };

      // Insert the variable node inline at the cursor.
      Transforms.insertNodes(editor, variableNode);

      // 2) Insert a small text node after it so there's a place for the cursor
      Transforms.insertNodes(editor, { text: ' ' });

      // 3) Move the selection to the node we just inserted
      // Editor.after(...) finds the next valid position in the document after our insertion
      const { selection } = editor;
      if (selection) {
        const after = Editor.after(editor, selection, { unit: 'offset' });
        if (after) {
          Transforms.select(editor, after); // place cursor there
        }
      }

      // 4) Focus the editor so we can continue typing right after the variable
      ReactEditor.focus(editor);
    },
    [editor],
  );

  const focusEditor = useCallback(() => {
    setTimeout(() => {
      if (!editor.selection) {
        // Move selection to end of editor
        Transforms.select(editor, Editor.end(editor, []));
      }
      // Focus the editor
      ReactEditor.focus(editor);
    }, 0);
  }, [editor]);

  function onClickBlock(variable) {
    insertVariable(variable);
    setShowVariableDialog(false);
    setJustInserted(true);
  }

  function onChange(newValue) {
    setValue(newValue);
    setFieldValue(name, JSON.stringify(newValue));
  }

  const handleAddVariableClick = () => {
    const buttonRect = addVariableButtonRef.current.getBoundingClientRect();
    const editorRect = editorRef.current?.getBoundingClientRect();

    if (editorRect) {
      setDialogPosition({
        top: buttonRect.top - editorRect.top - 200,
        left: buttonRect.left - editorRect.left,
      });
      setShowVariableDialog(true);
    }
  };

  const insertCondition = useCallback(
    (variable, operator, value) => {
      const conditionNode = {
        type: CONDITION_NODE_TYPE,
        data: {
          variable,
          operator,
          value,
        },
        children: [
          {
            type: 'paragraph',
            children: [{ text: '' }],
          },
        ],
      };

      Transforms.insertNodes(editor, conditionNode);
      const path = [...editor.selection.anchor.path.slice(0, -1), 0, 0];
      Transforms.select(editor, { path, offset: 0 });
      ReactEditor.focus(editor);
    },
    [editor],
  );
  /* 
  useEffect(() => {
    const handleClickOutside = event => {
      if (dialogRef.current && !dialogRef.current.contains(event.target) && showVariableDialog) {
        setShowVariableDialog(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showVariableDialog]); */
  /* 
  useEffect(() => {
    if (!showVariableDialog) {
      focusEditor();
    }
  }, [showVariableDialog, focusEditor]);

  useEffect(() => {
    if (justInserted) {
      focusEditor();
      setJustInserted(false);
    }
  }, [justInserted, focusEditor]); */

  return (
    <div style={{ position: 'relative' }}>
      <Slate editor={editor} initialValue={value} onChange={onChange}>
        <Editable
          ref={editorRef}
          className={clsx(
            `overflow-auto appearance-none block w-full py-1.5 pr-10 px-2 text-gray-800 rounded text-left border-none shadow-input focus:shadow-input-focus`,
            {
              'min-h-32': appearance === 'default',
              'min-h-16': appearance === 'small',
            },
          )}
          id={id}
          renderElement={props => (
            <ElementWithContext {...props} editor={editor} variables={variables} />
          )}
          renderLeaf={Leaf}
          onKeyDown={onKeyDown}
        />
      </Slate>

      <div className="absolute bottom-2 right-2 flex space-x-2">
        {/* <Button
          type="button"
          className="text-sm"
          size="small"
          appearance="secondary"
          onClick={() => {
            setShowConditionDialog(true);
          }}
        >
          <ChevronDownIcon className="w-4 h-4 mr-2" /> Add Condition
        </Button> */}

        <Button
          ref={addVariableButtonRef}
          type="button"
          className="text-sm"
          size="small"
          appearance="secondary"
          onClick={handleAddVariableClick}
        >
          <PlusIcon className="w-4 h-4 mr-2" /> Add Variable
        </Button>
      </div>

      <VariableDialog
        ref={dialogRef}
        onClickAdd={onClickBlock}
        variables={variables}
        isOpen={showVariableDialog}
        onClose={() => {
          setShowVariableDialog(false);
          focusEditor();
        }}
        position={dialogPosition}
        selectedIndex={selectedIndex}
        setSelectedIndex={setSelectedIndex}
        firstItemRef={firstVariableItemRef}
      />

      <EditConditionDialog
        isOpen={showConditionDialog}
        onClose={() => {
          setShowConditionDialog(false);
        }}
        onSave={(variable, operator, value) => {
          insertCondition(variable, operator, value);
          setShowConditionDialog(false);
        }}
        variables={variables}
      />
    </div>
  );
};

// EditConditionDialog component
const EditConditionDialog = ({
  isOpen,
  onClose,
  onSave,
  initialVariable,
  initialOperator = 'equals',
  initialValue = '',
  variables = [],
}) => {
  const [selectedVariable, setSelectedVariable] = useState(() => initialVariable || variables[0]);
  const [operator, setOperator] = useState(initialOperator);
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    if (isOpen && variables.length > 0) {
      setSelectedVariable(initialVariable || variables[0]);
      setOperator(initialOperator);
      setValue(initialValue);
    }
  }, [isOpen, initialVariable, initialOperator, initialValue, variables]);

  const operators = [
    { label: 'equals', value: 'equals' },
    { label: 'not equals', value: 'not_equals' },
    { label: 'contains', value: 'contains' },
    { label: 'not contains', value: 'not_contains' },
    { label: 'starts with', value: 'starts_with' },
    { label: 'ends with', value: 'ends_with' },
    { label: 'greater than', value: 'greater_than' },
    { label: 'less than', value: 'less_than' },
    { label: 'greater than or equal', value: 'greater_than_or_equal' },
    { label: 'less than or equal', value: 'less_than_or_equal' },
  ];

  return (
    <Dialog
      title="Add Condition"
      description="Add a condition to show or hide content based on variable values."
      open={isOpen}
      onCancel={onClose}
      confirmButton={{
        label: 'Save',
        onClick: () => onSave(selectedVariable, operator, value),
        appearance: 'primary',
      }}
      cancelButton={{
        label: 'Cancel',
        appearance: 'secondary',
      }}
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Variable</label>
          <select
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
            value={selectedVariable?.id}
            onChange={e => setSelectedVariable(variables.find(v => v.id === e.target.value))}
          >
            {variables.map(v => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Operator</label>
          <select
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
            value={operator}
            onChange={e => setOperator(e.target.value)}
          >
            {operators.map(op => (
              <option key={op.value} value={op.value}>
                {op.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Value</label>
          <input
            type="text"
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
            value={value}
            onChange={e => setValue(e.target.value)}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default RichTextarea;
